import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isAllRoomsName } from '../../shared/roomNameMatcher';
import { parseDealOptionNumber } from '../dealOptions/dealOptionsFormatters';
import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { type EgsApplyBlocker, isGateBlocker } from './egsBlockers';
import { getRatePlanName } from './egsRatePlans';
import { EGS_SLOTS, getGuestSlot, hasArchiveDisagreement, isArchivedRow } from './egsUtils';
import type { EgsGuestSlot, EgsRoomGroup } from './types';

export type { EgsApplyBlocker };

/**
 * svc-reservation stores one record per room rate holding all four guest slots in a single currency, while
 * Salesforce stores one row per guest type, so an apply is a read-modify-write of the whole record.
 */
export const EGS_WRITE_CURRENCY = 'AUD';

const FREE_OF_CHARGE = 'free of charge';
const PAID_DIRECTLY_TO_HOTEL = 'paid directly to the hotel';

export type EgsSlotOrigin = 'free_of_charge' | 'kept' | 'salesforce';

export interface EgsSlotPlan {
  cost: { after: number; before: number | null };
  origin: EgsSlotOrigin;
  sell: { after: number; before: number | null };
}

export type EgsArchiveDecision = 'archived' | 'live';

export interface EgsGateResolution {
  /** Which side of a `payable_at_property_conflict` the single stored flag should take. */
  payableAtProperty?: boolean;
  /** The Salesforce row that wins a contested guest type, by row id. */
  slotWinnerRowIds: Partial<Record<EgsGuestSlot, string>>;
}

export const EMPTY_GATE_RESOLUTION: EgsGateResolution = { slotWinnerRowIds: {} };

export interface EgsSlotConflictOption {
  collectedBy: string;
  cost: number | undefined;
  roomLabel: string;
  rowId: string;
  sell: number | undefined;
}

export interface EgsSlotConflict {
  options: Array<EgsSlotConflictOption>;
  resolvedRowId?: string;
  slot: EgsGuestSlot;
}

export interface EgsArchiveDisagreement {
  decision?: EgsArchiveDecision;
  name: string;
  rowId: string;
}

// One question for both causes, since a room rate stores one `payable_at_property` flag for all four slots.
export interface EgsPayableConflict {
  affectsKeptCharges: boolean;
  resolvedTo?: boolean;
  rowsDisagree: boolean;
}

// Answered gates stay listed, so the CM can see what they chose and change it.
export interface EgsPlanGates {
  archiveDisagreements: Array<EgsArchiveDisagreement>;
  payableConflict?: EgsPayableConflict;
  slotConflicts: Array<EgsSlotConflict>;
}

export interface RoomRateEgsPlan {
  blocker?: EgsApplyBlocker;
  /** Only where the blocker is slot-level, so a record-wide refusal cannot name a guest type it does not mean. */
  blockerSlots?: Array<EgsGuestSlot>;
  existingCurrency?: string;
  existingRecordId?: string;
  gates: EgsPlanGates;
  /** False when the merged record is identical to what is already stored, so there is nothing to send. */
  hasChange: boolean;
  /** True when Salesforce says every slot is free, where creating an all-zero record is worse than none. */
  isAllFreeOfCharge: boolean;
  operation: 'create' | 'update';
  payableAtProperty: boolean;
  ratePlanName: string;
  /** Only the Salesforce records that put a row into this write, never one that lost every slot. */
  roomLabels: Array<string>;
  roomRateId: string;
  roomTypeId: string;
  roomTypeName: string;
  slots: Record<EgsGuestSlot, EgsSlotPlan>;
  /** Guest types Salesforce priced at nothing while charging for them, so the write left them alone. */
  unpricedSlots: Array<EgsGuestSlot>;
}

// Everything the write reads off a row, so two rows sharing a fingerprint are duplicates, not a conflict.
function getRowFingerprint(row: OpportunityRelatedExtraGuestSurcharge) {
  return JSON.stringify([
    (row.collected_by__c ?? '').trim().toLowerCase(),
    parseDealOptionNumber(row.converted_cost__c) ?? null,
    parseDealOptionNumber(row.sell_price_aud__c) ?? null,
  ]);
}

// A decision replaces the signal for that row only; rows nobody was asked about keep reading the flag.
function isRowArchived(row: OpportunityRelatedExtraGuestSurcharge, archiveByRowId: Record<string, EgsArchiveDecision>) {
  const decision = archiveByRowId[row.id];

  return decision ? decision === 'archived' : isArchivedRow(row);
}

/**
 * A record naming this room outranks a blanket "All Rooms" one, which is the default it was written to
 * override. At equal specificity, rows that agree merge and rows that disagree block.
 */
function resolveLiveRowsBySlot(
  groups: Array<EgsRoomGroup>,
  archiveByRowId: Record<string, EgsArchiveDecision>,
  resolution: EgsGateResolution,
) {
  const candidatesBySlot = new Map<
    EgsGuestSlot,
    Array<{ isAllRooms: boolean; roomLabel: string; row: OpportunityRelatedExtraGuestSurcharge }>
  >();

  for (const group of groups) {
    const isAllRooms = isAllRoomsName(group.roomLabel);

    for (const row of group.rows) {
      const slot = getGuestSlot(row.extra_guest_type__c);

      if (slot && !isRowArchived(row, archiveByRowId)) {
        candidatesBySlot.set(slot, [
          ...(candidatesBySlot.get(slot) ?? []),
          { isAllRooms, roomLabel: group.roomLabel, row },
        ]);
      }
    }
  }

  const conflictingSlots: Array<EgsGuestSlot> = [];
  const contributingLabels = new Set<string>();
  const rowsBySlot = new Map<EgsGuestSlot, OpportunityRelatedExtraGuestSurcharge>();
  const slotConflicts: Array<EgsSlotConflict> = [];

  // Slot order rather than insertion order, so the reported conflicts read consistently.
  for (const slot of EGS_SLOTS) {
    const candidates = candidatesBySlot.get(slot) ?? [];

    if (candidates.length === 0) {
      continue;
    }

    const specific = candidates.filter((candidate) => !candidate.isAllRooms);
    const winners = specific.length > 0 ? specific : candidates;

    const byFingerprint = new Map<string, (typeof winners)[number]>();
    winners.forEach((candidate) => {
      const fingerprint = getRowFingerprint(candidate.row);

      if (!byFingerprint.has(fingerprint)) {
        byFingerprint.set(fingerprint, candidate);
      }
    });

    if (byFingerprint.size > 1) {
      const options = Array.from(byFingerprint.values());
      const picked = options.find((candidate) => candidate.row.id === resolution.slotWinnerRowIds[slot]);

      slotConflicts.push({
        options: options.map(({ roomLabel, row }) => ({
          collectedBy: (row.collected_by__c ?? '').trim(),
          cost: parseDealOptionNumber(row.converted_cost__c),
          roomLabel,
          rowId: row.id,
          sell: parseDealOptionNumber(row.sell_price_aud__c),
        })),
        resolvedRowId: picked?.row.id,
        slot,
      });

      if (!picked) {
        conflictingSlots.push(slot);
        winners.forEach((candidate) => contributingLabels.add(candidate.roomLabel));
        continue;
      }

      rowsBySlot.set(slot, picked.row);
      contributingLabels.add(picked.roomLabel);
      continue;
    }

    rowsBySlot.set(slot, winners[0].row);
    contributingLabels.add(winners[0].roomLabel);
  }

  return { conflictingSlots, contributingLabels, rowsBySlot, slotConflicts };
}

function isFreeOfCharge(row: OpportunityRelatedExtraGuestSurcharge) {
  return (row.collected_by__c ?? '').trim().toLowerCase() === FREE_OF_CHARGE;
}

/**
 * Free of Charge rows collect nothing and are excluded, so a free infant beside a paid-at-property adult is
 * not a conflict.
 */
function resolvePayableAtProperty(rows: Array<OpportunityRelatedExtraGuestSurcharge>, override: boolean | undefined) {
  const flags = new Set(
    rows
      .filter((row) => !isFreeOfCharge(row))
      .map((row) => (row.collected_by__c ?? '').trim().toLowerCase() === PAID_DIRECTLY_TO_HOTEL),
  );
  const isContested = flags.size > 1;

  // Only applied where the rows disagree, so a stale answer cannot flip an uncontested record.
  if (isContested && override !== undefined) {
    return { hasConflict: false, isContested, payableAtProperty: override };
  }

  return { hasConflict: isContested, isContested, payableAtProperty: flags.has(true) };
}

/**
 * A chargeable row with no Sell Price is silence about that guest type, not a price of zero, so the slot is
 * skipped and keeps what the room rate has. Contrast `getMissingCostSlots`, where a price does exist and every
 * partial write would misstate margin, which is why that one refuses instead.
 */
function getUnpricedSlots(rowsBySlot: Map<EgsGuestSlot, OpportunityRelatedExtraGuestSurcharge>) {
  return EGS_SLOTS.filter((slot) => {
    const row = rowsBySlot.get(slot);

    return !!row && !isFreeOfCharge(row) && (parseDealOptionNumber(row.sell_price_aud__c) ?? 0) <= 0;
  });
}

/**
 * A zero cost is legitimate; an absent one writes cost 0 beside a real sell price and overstates margin. Read
 * per slot rather than over the pooled rows, so the refusal can name the guest type whose cost is blank, and
 * blind to a skipped slot: nothing is written for it, so there is no margin left to misstate.
 */
function getMissingCostSlots(
  rowsBySlot: Map<EgsGuestSlot, OpportunityRelatedExtraGuestSurcharge>,
  skippedSlots: Set<EgsGuestSlot>,
) {
  return EGS_SLOTS.filter((slot) => {
    const row = rowsBySlot.get(slot);

    return (
      !!row &&
      !skippedSlots.has(slot) &&
      !isFreeOfCharge(row) &&
      parseDealOptionNumber(row.converted_cost__c) === undefined
    );
  });
}

function buildSlotPlan(
  row: OpportunityRelatedExtraGuestSurcharge | undefined,
  existing: App.ExtraGuestSurcharges | undefined,
  slot: EgsGuestSlot,
): EgsSlotPlan {
  const beforeSell = existing ? existing[`${slot}_amount`] : null;
  const beforeCost = existing ? existing[`${slot}_cost`] : null;

  if (!row) {
    return {
      cost: { after: beforeCost ?? 0, before: beforeCost },
      origin: 'kept',
      sell: { after: beforeSell ?? 0, before: beforeSell },
    };
  }

  if (isFreeOfCharge(row)) {
    return { cost: { after: 0, before: beforeCost }, origin: 'free_of_charge', sell: { after: 0, before: beforeSell } };
  }

  return {
    // Both fields are already AUD, so the apply does no FX of its own.
    cost: { after: parseDealOptionNumber(row.converted_cost__c) ?? 0, before: beforeCost },
    origin: 'salesforce',
    sell: { after: parseDealOptionNumber(row.sell_price_aud__c) ?? 0, before: beforeSell },
  };
}

// Absent compares as zero, so an all-zero plan is not a change. Cost counts too, since it moves margin.
export function isSlotChanged(slotPlan: EgsSlotPlan) {
  return slotPlan.sell.after !== (slotPlan.sell.before ?? 0) || slotPlan.cost.after !== (slotPlan.cost.before ?? 0);
}

// Currency and `payable_at_property` are record-wide, so they reinterpret a kept charge without touching it.
function hasKeptCharge(slots: Record<EgsGuestSlot, EgsSlotPlan>) {
  return EGS_SLOTS.some(
    (slot) => slots[slot].origin === 'kept' && (slots[slot].sell.after > 0 || slots[slot].cost.after > 0),
  );
}

/** The blocker, plus the guest types that caused it where the cause is slot-level rather than record-wide. */
interface EgsResolvedBlock {
  blocker?: EgsApplyBlocker;
  blockerSlots?: Array<EgsGuestSlot>;
}

function resolveBlocker({
  conflictingSlots,
  existingRecords,
  hasPayableConflict,
  hasUndecidedArchiveRow,
  hasUnresolvedPayableMismatch,
  missingCostSlots,
  slots,
}: {
  conflictingSlots: Array<EgsGuestSlot>;
  existingRecords: Array<App.ExtraGuestSurcharges>;
  hasPayableConflict: boolean;
  hasUndecidedArchiveRow: boolean;
  hasUnresolvedPayableMismatch: boolean;
  missingCostSlots: Array<EgsGuestSlot>;
  slots: Record<EgsGuestSlot, EgsSlotPlan>;
}): EgsResolvedBlock {
  // Ahead of the slot conflict, which a disagreeing row usually causes; the root cause is the useful one.
  if (hasUndecidedArchiveRow) {
    return { blocker: 'archive_disagreement' };
  }

  if (conflictingSlots.length > 0) {
    return { blocker: 'conflicting_slots' };
  }

  // Pricing reads the first record per room rate and the query has no ORDER BY, so merging into one of
  // two would be a guess about which is live.
  if (existingRecords.length > 1) {
    return { blocker: 'multiple_existing_records' };
  }

  if (hasPayableConflict) {
    return { blocker: 'payable_at_property_conflict' };
  }

  const [existing] = existingRecords;

  // The record's currency governs sell as well as cost, so merging AUD into a vendor-currency record
  // reprices the slots being left alone. No answer makes this safe, unlike the payable flag below.
  if (existing && existing.currency !== EGS_WRITE_CURRENCY && hasKeptCharge(slots)) {
    return { blocker: 'currency_mismatch' };
  }

  if (hasUnresolvedPayableMismatch) {
    return { blocker: 'payable_at_property_mismatch' };
  }

  // Every offending slot, not the first: fixing one and being refused again for the next reads as no progress.
  if (missingCostSlots.length > 0) {
    return { blocker: 'missing_cost_price', blockerSlots: missingCostSlots };
  }

  return {};
}

export function buildRoomRateEgsPlan({
  archiveByRowId = {},
  egsByRoomRateId,
  groups,
  resolution = EMPTY_GATE_RESOLUTION,
  selection,
}: {
  archiveByRowId?: Record<string, EgsArchiveDecision>;
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  groups: Array<EgsRoomGroup>;
  resolution?: EgsGateResolution;
  selection: RoomRateSelection;
}): RoomRateEgsPlan {
  const roomRateId = String(selection.roomRate.id);
  const existingRecords = egsByRoomRateId[roomRateId] ?? [];
  const [existing] = existingRecords;
  // Pooled, since a per-record view cannot see a guest type that two records both price.
  const rows = groups.flatMap((group) => group.rows);
  const {
    conflictingSlots,
    contributingLabels,
    rowsBySlot: liveRowsBySlot,
    slotConflicts,
  } = resolveLiveRowsBySlot(groups, archiveByRowId, resolution);
  const liveRows = Array.from(liveRowsBySlot.values());
  const {
    hasConflict,
    isContested,
    payableAtProperty: fromSalesforce,
  } = resolvePayableAtProperty(liveRows, resolution.payableAtProperty);

  const archiveDisagreements = rows.filter(hasArchiveDisagreement).map((row) => ({
    decision: archiveByRowId[row.id],
    name: row.name ?? '',
    rowId: row.id,
  }));

  // An unusable row is the same as no row. Deliberately not a new origin: `hasKeptCharge` reads
  // `origin === 'kept'`, so anything else would hide these slots from the currency guard.
  const unpricedSlots = getUnpricedSlots(liveRowsBySlot);
  const skippedSlots = new Set(unpricedSlots);
  const slots = Object.fromEntries(
    EGS_SLOTS.map((slot) => [
      slot,
      buildSlotPlan(skippedSlots.has(slot) ? undefined : liveRowsBySlot.get(slot), existing, slot),
    ]),
  ) as Record<EgsGuestSlot, EgsSlotPlan>;

  // Record-wide like the currency guard, but both answers are real values, so it is asked not refused.
  const isPayableMismatched = !!existing && !!existing.payable_at_property !== fromSalesforce && hasKeptCharge(slots);
  const payableAtProperty = isPayableMismatched ? (resolution.payableAtProperty ?? fromSalesforce) : fromSalesforce;

  const { blocker, blockerSlots } = resolveBlocker({
    conflictingSlots,
    existingRecords,
    hasPayableConflict: hasConflict,
    hasUndecidedArchiveRow: archiveDisagreements.some((disagreement) => !disagreement.decision),
    hasUnresolvedPayableMismatch: isPayableMismatched && resolution.payableAtProperty === undefined,
    missingCostSlots: getMissingCostSlots(liveRowsBySlot, skippedSlots),
    slots,
  });

  return {
    blocker,
    blockerSlots,
    existingCurrency: existing?.currency,
    existingRecordId: existing?.id,
    gates: {
      archiveDisagreements,
      payableConflict:
        isContested || isPayableMismatched
          ? {
              affectsKeptCharges: isPayableMismatched,
              resolvedTo: resolution.payableAtProperty,
              rowsDisagree: isContested,
            }
          : undefined,
      slotConflicts,
    },
    hasChange: EGS_SLOTS.some((slot) => isSlotChanged(slots[slot])),
    isAllFreeOfCharge: liveRows.length > 0 && liveRows.every(isFreeOfCharge),
    operation: existing ? 'update' : 'create',
    payableAtProperty,
    ratePlanName: getRatePlanName(selection),
    unpricedSlots,
    roomLabels: groups.map((group) => group.roomLabel).filter((roomLabel) => contributingLabels.has(roomLabel)),
    roomRateId,
    roomTypeId: String(selection.roomType.id),
    roomTypeName: selection.roomType.name,
    slots,
  };
}

/**
 * Keyed by room rate rather than by record: two records can target the same one, and iterating records first
 * would send both as independent writes where the later wins.
 */
export function buildEgsApplyPlans({
  archiveByRowId = {},
  egsByRoomRateId,
  gateByRoomRateId = {},
  roomGroups,
  selectionsByRoomLabel,
}: {
  archiveByRowId?: Record<string, EgsArchiveDecision>;
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  gateByRoomRateId?: Record<string, EgsGateResolution>;
  roomGroups: Array<EgsRoomGroup>;
  selectionsByRoomLabel: Record<string, Array<RoomRateSelection>>;
}): Array<RoomRateEgsPlan> {
  const byRoomRateId = new Map<string, { groups: Array<EgsRoomGroup>; selection: RoomRateSelection }>();

  for (const group of roomGroups) {
    for (const selection of selectionsByRoomLabel[group.roomLabel] ?? []) {
      const roomRateId = String(selection.roomRate.id);
      const entry = byRoomRateId.get(roomRateId);

      if (entry) {
        entry.groups.push(group);
      } else {
        byRoomRateId.set(roomRateId, { groups: [group], selection });
      }
    }
  }

  return Array.from(byRoomRateId.values(), ({ groups, selection }) =>
    buildRoomRateEgsPlan({
      archiveByRowId,
      egsByRoomRateId,
      groups,
      resolution: gateByRoomRateId[String(selection.roomRate.id)] ?? EMPTY_GATE_RESOLUTION,
      selection,
    }),
  );
}

/**
 * `hasChange` treating absent as zero excludes an all-zero create: `check_in_surcharge_applies` is an OR over
 * the surcharge types, so a record of zeros paints a surcharge marker across the date pickers for nothing.
 */
export function isApplicable(plan: RoomRateEgsPlan) {
  return !plan.blocker && plan.hasChange;
}

// Blocked only until the CM answers, unlike the four refusals no answer can clear.
export function isWaitingOnGate(plan: RoomRateEgsPlan) {
  return !!plan.blocker && isGateBlocker(plan.blocker);
}

export type EgsNothingToApplyReason = 'all_free_of_charge' | 'already_matches';

/**
 * Answered at full reach: narrowing the selection can only move a slot back to what is stored, so nothing
 * changing at full reach means nothing changes at any lesser reach. A blocked plan still counts as writable,
 * since dropping its conflict may unblock it.
 */
export function getNothingToApplyReason(plansAtFullReach: Array<RoomRateEgsPlan>): EgsNothingToApplyReason | undefined {
  if (plansAtFullReach.length === 0) {
    return undefined;
  }

  if (plansAtFullReach.some((plan) => isApplicable(plan) || !!plan.blocker)) {
    return undefined;
  }

  return plansAtFullReach.every((plan) => plan.isAllFreeOfCharge) ? 'all_free_of_charge' : 'already_matches';
}

export function buildEgsPayload(plan: RoomRateEgsPlan): App.ExtraGuestSurcharges {
  return {
    adult_amount: plan.slots.adult.sell.after,
    adult_cost: plan.slots.adult.cost.after,
    child_amount: plan.slots.child.sell.after,
    child_cost: plan.slots.child.cost.after,
    currency: EGS_WRITE_CURRENCY,
    id: plan.existingRecordId,
    infant_amount: plan.slots.infant.sell.after,
    infant_cost: plan.slots.infant.cost.after,
    payable_at_property: plan.payableAtProperty,
    room_rate_id: plan.roomRateId,
    teenager_amount: plan.slots.teenager.sell.after,
    teenager_cost: plan.slots.teenager.cost.after,
  };
}
